import axios from 'axios';

export const PURCHASE_DRAFT_STATUS = 'rascunho';

const NOT_A_DRAFT_DETAILS = new Set([
  'This is not a draft',
  'Esta requisição não é um rascunho.',
]);

export type PurchaseDraftFormInput = {
  title?: string | null;
  object?: string | null;
  itemsDescription?: string | null;
  description?: string | null;
  urgency?: string | null;
  priority?: string | null;
  includePriority?: boolean;
  workflowId?: string | null;
  groupId?: string | null;
  requesterId?: number | null;
};

type AxiosLikeError = {
  response?: { data?: { detail?: unknown } };
};

export function isPurchaseDraftStatus(status: unknown): boolean {
  return String(status ?? '').trim().toLowerCase() === PURCHASE_DRAFT_STATUS;
}

export function isNotADraftApiError(error: unknown): boolean {
  const detail = (error as AxiosLikeError).response?.data?.detail;
  return typeof detail === 'string' && NOT_A_DRAFT_DETAILS.has(detail);
}

export function purchaseDraftsCollectionUrl(apiUrl: string): string {
  return `${apiUrl}/api/purchases/drafts`;
}

export function purchaseDraftItemUrl(apiUrl: string, draftId: number): string {
  return `${apiUrl}/api/purchases/drafts/${draftId}`;
}

export function purchaseDraftFinalizeUrl(apiUrl: string, draftId: number): string {
  return `${apiUrl}/api/purchases/drafts/${draftId}/finalize`;
}

export function buildPurchaseDraftPayload(input: PurchaseDraftFormInput): Record<string, unknown> {
  const title =
    String(input.title || '').trim() ||
    String(input.object || '').trim() ||
    'Rascunho sem título';
  const payload: Record<string, unknown> = {
    status: PURCHASE_DRAFT_STATUS,
    title,
    description: input.description || input.itemsDescription || '',
  };
  if (input.object) payload.object = input.object;
  if (input.itemsDescription) payload.items_description = input.itemsDescription;
  if (input.urgency) payload.urgency = input.urgency;
  if (input.includePriority && input.priority) payload.priority = input.priority;
  if (input.workflowId) payload.workflow_id = parseInt(input.workflowId, 10);
  if (input.groupId) payload.assigned_group_id = parseInt(input.groupId, 10);
  if (input.requesterId != null) payload.requester_id = input.requesterId;
  return payload;
}

export const NOT_A_DRAFT_SUBMIT_MESSAGE =
  'Esta requisição já não é um rascunho. Feche e reabra.';

export type SavePurchaseDraftOptions = {
  /** Só o botão "Salvar Como Rascunho" deve recriar. Autosave nunca. */
  recreateIfNotDraft?: boolean;
};

export type SavePurchaseDraftResult = {
  id: number | null;
  created: boolean;
  skipped: boolean;
};

export function throwNotADraftForSubmit(error: unknown): never {
  if (isNotADraftApiError(error)) {
    const wrapped = new Error(NOT_A_DRAFT_SUBMIT_MESSAGE) as Error & {
      response?: { data?: { detail: string } };
    };
    wrapped.response = { data: { detail: NOT_A_DRAFT_SUBMIT_MESSAGE } };
    throw wrapped;
  }
  throw error;
}

export async function savePurchaseDraftRecord(
  apiUrl: string,
  draftId: number | null,
  payload: Record<string, unknown>,
  options: SavePurchaseDraftOptions = {}
): Promise<SavePurchaseDraftResult> {
  const recreateIfNotDraft = options.recreateIfNotDraft === true;
  const body = { ...payload, status: PURCHASE_DRAFT_STATUS };
  if (draftId) {
    try {
      const { data } = await axios.put(purchaseDraftItemUrl(apiUrl, draftId), body);
      return { id: Number(data?.id ?? draftId), created: false, skipped: false };
    } catch (error) {
      if (!isNotADraftApiError(error)) {
        throw error;
      }
      if (!recreateIfNotDraft) {
        return { id: null, created: false, skipped: true };
      }
    }
  }

  const { data } = await axios.post(purchaseDraftsCollectionUrl(apiUrl), body);
  if (!isPurchaseDraftStatus(data?.status)) {
    throw new Error('O servidor não criou um rascunho.');
  }
  return { id: Number(data.id), created: true, skipped: false };
}
